import { Directive, ElementRef, HostListener, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: 'input[appDecimalInput]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DecimalInputDirective),
      multi: true,
    },
  ],
})
export class DecimalInputDirective implements ControlValueAccessor {
  /** マイナス禁止 */
  @Input() positiveOnly = false;

  /** モデル（decimal小数文字列）の小数桁 */
  @Input() scale = 2;

  /** 表示時小数桁（未指定なら scale） */
  @Input() displayScale: number | undefined = undefined;

  /** 3桁区切り（blur時のみ） */
  @Input() useThousandsSeparator = true;

  /** %として入力・表示するか（モデルは小数のまま） */
  @Input() asPercent = false;

  /** %表示時の小数桁（例: 2 → 12.34%） */
  @Input() percentScale = 2;

  /** placeholder（任意） */
  @Input() placeholder = '';

  private disabled = false;

  /** モデル値（DECIMAL文字列、カンマ無し、小数=0.1234） */
  private modelValue: string | null = null;

  private onChange: (v: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private readonly elRef: ElementRef<HTMLInputElement>) {}

  // ----- CVA -----
  writeValue(value: unknown): void {
    const v = (value === null || value === undefined || value === '') ? null : String(value);
    this.modelValue = v;
    this.renderDisplay(this.formatForDisplay(v));
  }

  registerOnChange(fn: (v: string | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.el.disabled = isDisabled;
  }

  // ----- Events -----
  @HostListener('focus')
  handleFocus() {
    if (this.disabled) return;

    // readonly は入力できないので、フォーカス時の表示切替を抑止したいならここで return
    // if (this.el.nativeElement.readOnly) return;

    let value = this.modelValue;

    // 念のためモデルが負なら正に補正
    if (this.positiveOnly && value?.startsWith('-')) {
      value = value.slice(1);
      this.modelValue = value;
      this.onChange(value);
    }

    if (this.asPercent) {
      const pct = value ? this.shiftDecimal(value, +2) : null;
      this.renderDisplay(pct ?? '');
    } else {
      // blur後のカンマ表示を剥がして “生値” に寄せる
      this.renderDisplay(value ?? this.stripCommas(this.el.value));
    }
  }

  @HostListener('input', ['$event'])
  handleInput(ev: Event) {
    if (this.disabled) return;
    if (this.el.readOnly) return;

    const raw = (ev.target as HTMLInputElement).value;
    const sanitized = this.sanitizeTyping(raw);

    // DOM と表示をサニタイズ後に統一
    if (sanitized !== raw) {
      this.el.value = sanitized;
    }

    // 未確定入力は null 扱い
    if (sanitized === '' || sanitized === '.' || sanitized === '-' || sanitized === '-.') {
      this.modelValue = null;
      this.onChange(null);
      return;
    }

    const typing = this.stripCommas(sanitized);
    let model = this.asPercent ? this.shiftDecimal(typing, -2) : typing;

    if (this.positiveOnly && model.startsWith('-')) {
      model = model.slice(1);
    }

    this.modelValue = model;
    this.onChange(model);
  }

  @HostListener('blur')
  handleBlur() {
    this.onTouched();
    if (this.disabled) return;

    // blur時に「確定丸め」してモデル・表示を整える
    if (this.asPercent) {
      const pctNormalized = this.normalizeToScale(this.stripCommas(this.el.value), this.percentScale);
      const decimal = pctNormalized ? this.shiftDecimal(pctNormalized, -2) : null;
      const decimalNormalized = this.normalizeToScale(decimal, this.scale);

      this.modelValue = decimalNormalized;
      this.onChange(decimalNormalized);
      this.renderDisplay(this.formatForDisplay(decimalNormalized));
      return;
    }

    const normalized = this.normalizeToScale(this.modelValue, this.scale);
    this.modelValue = normalized;
    this.onChange(normalized);
    this.renderDisplay(this.formatForDisplay(normalized));
  }

  // ----- DOM helpers -----
  private get el(): HTMLInputElement { return this.elRef.nativeElement; }

  private renderDisplay(v: string) {
    // placeholder 反映（任意）
    if (this.placeholder) this.el.placeholder = this.placeholder;
    this.el.value = v ?? '';
  }

  // ---------- formatting / parsing ----------

  private formatForDisplay(modelDecimal: string | null): string {
    if (!modelDecimal) return '';

    if (this.asPercent) {
      const pct = this.shiftDecimal(modelDecimal, +2);
      const pctNorm = this.normalizeToScale(pct, this.percentScale);
      return this.formatPlainDecimal(pctNorm);
    }

    const ds = (this.displayScale !== undefined && this.displayScale !== null)
      ? this.displayScale
      : this.scale;

    const norm = this.normalizeToScale(modelDecimal, ds);
    return this.formatPlainDecimal(norm);
  }

  private formatPlainDecimal(value: string | null): string {
    if (!value) return '';
    const { sign, intPart, fracPart } = this.splitDecimal(value);

    const intFormatted = this.useThousandsSeparator
      ? this.addThousandsSeparator(intPart)
      : intPart;

    return fracPart.length > 0
      ? `${sign}${intFormatted}.${fracPart}`
      : `${sign}${intFormatted}`;
  }

  /** 10^places ぶん小数点を移動（places=+2 なら *100、-2 なら /100） */
  private shiftDecimal(value: string, places: number): string {
    let v = value.trim();

    if (this.positiveOnly && v.startsWith('-')) {
      v = v.slice(1);
    }

    const sign = v.startsWith('-') ? '-' : '';
    const body = sign ? v.slice(1) : v;

    const [i = '0', f = ''] = body.split('.');
    const digits = (i + f).replace(/^0+(?=\d)/, '') || '0';
    const fracLen = f.length;


    const pointFromRight = fracLen;

    const newPointFromRight = pointFromRight - places;

    let outInt = '';
    let outFrac = '';

    if (newPointFromRight <= 0) {

      outInt = digits + '0'.repeat(-newPointFromRight);
      outFrac = '';
    } else if (newPointFromRight >= digits.length) {

      outInt = '0';
      outFrac = '0'.repeat(newPointFromRight - digits.length) + digits;
    } else {
      const cut = digits.length - newPointFromRight;
      outInt = digits.slice(0, cut) || '0';
      outFrac = digits.slice(cut);
    }

    return sign + (outFrac ? `${outInt}.${outFrac}` : outInt);
  }

  private sanitizeTyping(s: string): string {
    // 入力中はカンマを許可しない（blur整形で付ける）
    let v = s.replace(/[^0-9.\-]/g, '');

    if (this.positiveOnly) {
      v = v.replace(/-/g, '');
    }

    const minus = !this.positiveOnly && v.startsWith('-') ? '-' : '';
    v = v.replace(/-/g, '');

    const parts = v.split('.');
    if (parts.length > 1) v = parts[0] + '.' + parts.slice(1).join('');

    return minus + v;
  }

  private stripCommas(s: unknown): string {
    return String(s ?? '').replace(/,/g, '');
  }

  private splitDecimal(value: string): { sign: string; intPart: string; fracPart: string } {
    const v = value.trim();
    const sign = v.startsWith('-') ? '-' : '';
    const body = sign ? v.slice(1) : v;
    const [iRaw, fRaw = ''] = body.split('.');
    const intPart = (iRaw ?? '').replace(/^0+(?=\d)/, '') || '0';
    const fracPart = fRaw ?? '';
    return { sign, intPart, fracPart };
  }

  private addThousandsSeparator(intPart: string): string {
    return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /** 四捨五入 half-up で scale 桁に正規化（BigIntベース） */
  private normalizeToScale(value: string | null, scale: number): string | null {
    if (value == null) return null;

    let v = String(value);

    if (this.positiveOnly && v.startsWith('-')) {
      v = v.slice(1);
    }

    const stripped = this.stripCommas(v).trim();
    if (stripped === '' || stripped === '-' || stripped === '.' || stripped === '-.') return null;

    const { sign: origSign, intPart, fracPart } = this.splitDecimal(stripped);
    const sign = this.positiveOnly ? '' : origSign;

    const s = Math.max(0, Math.floor(scale));

    const fracPadded = (fracPart + '0'.repeat(s + 1)).slice(0, s + 1);
    const keep = fracPadded.slice(0, s);
    const roundDigit = fracPadded.slice(s, s + 1);

    const combined = BigInt((intPart || '0') + (keep || ''));
    const shouldRoundUp = roundDigit !== '' && roundDigit >= '5';
    const rounded = shouldRoundUp ? combined + 1n : combined;

    let roundedStr = rounded.toString();

    if (s === 0) return sign ? '-' + roundedStr : roundedStr;

    if (roundedStr.length <= s) {
      roundedStr = '0'.repeat(s + 1 - roundedStr.length) + roundedStr;
    }

    const intOut = roundedStr.slice(0, -s) || '0';
    const fracOut = roundedStr.slice(-s);

    return sign ? `-${intOut}.${fracOut}` : `${intOut}.${fracOut}`;
  }
}
